import React, { forwardRef, useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import { Input as CoreInput, Textarea as CoreTextarea } from '../../../../core';
import { debounce, isNil } from 'lodash';
import { TYPE_TEXT } from '../../../../constants';
import { GetValidationPattern, isEqualLowerCase } from '../../../../utils/ValidationUtil';
import { formatClassName } from '../../../../utils';
import { i18n } from '../../../../i18n/translate';

/** Common type for any form field value. */
type FieldValue = string | number | boolean | string[] | null | undefined;

export interface FormControlProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'onChange' | 'onBlur' | 'onFocus'
> {
  activeLanguage?: string;
  caption?: string;
  as?: string;
  dataValue?: FieldValue;
  disableSubmit?: boolean;
  errorMessage?: string;
  languages?: Array<Record<string, string>>;
  onBlur?: (value: FieldValue) => void;
  onChange?: (value: FieldValue) => void;
  onFocus?: () => void;
  onValueChange?: (obj: Record<string, FieldValue>) => void;
  runValidation?: boolean;
  scrollContainerId?: string;
  searchField?: React.ReactNode;
  setError?: (error: boolean, message?: string) => void;
  valueChangeDelay?: number;
  valueOverride?: boolean;
  ignorePattern?: boolean;
  isValid?: () => boolean;
  renderAsLegacy?: boolean;
}

/** Imperative handle exposed via ref — mirrors the class API used by FormField. */
export interface FormControlHandle {
  isValid: () => boolean;
  checkError: () => void;
}

export const FormControl = forwardRef<FormControlHandle, FormControlProps>(
  (
    {
      activeLanguage,
      as,
      caption,
      className,
      dataValue,
      disableSubmit,
      errorMessage,
      languages,
      name,
      onBlur: onBlurProp,
      onChange: onChangeProp,
      onFocus: onFocusProp,
      onValueChange,
      pattern,
      required,
      runValidation,
      scrollContainerId,
      searchField,
      setError,
      type,
      valueChangeDelay = 1000,
      valueOverride,
      ignorePattern,
      ...rest
    },
    ref
  ) => {
    const [value, setValue] = useState<FieldValue>(!isNil(dataValue) ? dataValue : '');
    const isDirty = useRef(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>();
    const resolvedPattern = useRef<string | undefined>(ignorePattern ? undefined : GetValidationPattern(type, pattern));

    // Keep pattern up-to-date
    useEffect(() => {
      if (!ignorePattern) {
        resolvedPattern.current = GetValidationPattern(type, pattern);
      }
    }, [pattern, type, ignorePattern]);

    const debouncedValueChange = useRef(
      debounce(
        (val: FieldValue) => {
          if (name) onValueChange?.({ [name]: val });
        },
        valueChangeDelay ?? 1000,
        { leading: false, trailing: true }
      )
    ).current;

    // Sync incoming dataValue when field hasn't been modified by the user (handles async-loaded edit forms)
    useEffect(() => {
      if (!isDirty.current && !isNil(dataValue)) {
        setValue(dataValue);
      }
    }, [dataValue]);

    // Sync external dataValue if valueOverride — only when dataValue is actually defined
    useEffect(() => {
      if (valueOverride && !isNil(dataValue) && dataValue !== value) {
        onChange(dataValue, true);
      }
    }, [dataValue, valueOverride]);

    const onChange = useCallback(
      (val: FieldValue, skipValueChange?: boolean) => {
        isDirty.current = true;
        onChangeProp?.(val);
        if (!skipValueChange) debouncedValueChange(val);
        setValue(val);
      },
      [onChangeProp, debouncedValueChange]
    );

    const isValid = useCallback((): boolean => {
      const input = inputRef.current;
      if (!input) return true;
      if (rest.isValid && typeof rest.isValid === 'function') {
        const valid = rest.isValid?.() ?? false;
        if (!valid) {
          input.setCustomValidity(i18n.t('Please enter a valid value'));
        } else {
          input.setCustomValidity('');
        }
        return valid;
      }
      return input.validity.valid;
    }, [rest.isValid]);

    const checkError = useCallback(() => {
      const input = inputRef.current;
      if (!input) return;
      let message: string | null = null;
      const valid = isValid();
      const fieldLabel = caption || name;
      const isEmailField = type?.toLowerCase() === 'email';
      const isEmailFormatError = isEmailField && (input.validity.typeMismatch || input.validity.patternMismatch);

      if (input.validity.valueMissing) {
        message = fieldLabel
          ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('This field is required');
      } else if (isEmailFormatError) {
        message = fieldLabel
          ? i18n.t('Enter a valid email address for %s in the format name@example.com.', {
              postProcess: 'sprintf',
              sprintf: [fieldLabel],
            })
          : i18n.t('Enter a valid email address in the format name@example.com.');
      } else if (!input.validity.valid) {
        message = fieldLabel
          ? i18n.t('The value entered for %s is invalid', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('The value you entered is invalid');
      }

      if (input.required && isEqualLowerCase(type, TYPE_TEXT) && String(value).trim().length === 0) {
        const requiredMessage = fieldLabel
          ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('This field is required');
        setError?.(true, requiredMessage);
        return;
      }

      if (!message && isEmailField) {
        const emailPattern = new RegExp(GetValidationPattern(type));
        if (!emailPattern.test(String(value))) {
          message = fieldLabel
            ? i18n.t('Enter a valid email address for %s in the format name@example.com.', {
                postProcess: 'sprintf',
                sprintf: [fieldLabel],
              })
            : i18n.t('Enter a valid email address in the format name@example.com.');
        }
      }

      setError?.(!valid, message ?? undefined);
    }, [isValid, caption, name, type, value, setError]);

    // Expose class-compatible imperative API
    useImperativeHandle(ref, () => ({ isValid, checkError }), [isValid, checkError]);

    const onFormChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange(e.currentTarget.value);
      },
      [onChange]
    );

    const onBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onBlurProp?.(value);
        checkError();
        if (!e?.currentTarget?.value) return;
        if (e.currentTarget.value !== dataValue) {
          if (name) onValueChange?.({ [name]: e.currentTarget.value });
          debouncedValueChange.cancel();
        }
      },
      [onBlurProp, value, checkError, dataValue, onValueChange, name, debouncedValueChange]
    );

    const onFocus = useCallback(() => {
      onFocusProp?.();
    }, [onFocusProp]);

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if (disableSubmit && e.key === 'Enter') e.preventDefault();
      },
      [disableSubmit]
    );

    const newClassName = formatClassName([className, { 'p-r-10': as === 'textarea' }]);

    if (as === 'textarea') {
      return (
        <CoreTextarea
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          className={newClassName}
          data-field="input-field"
          name={name}
          required={required}
          onBlur={onBlur}
          onChange={onFormChange}
          onFocus={onFocus}
          onKeyPress={handleKeyPress}
          value={String(value ?? '')}
        />
      );
    }

    return (
      <CoreInput
        {...rest}
        className={newClassName}
        data-field="input-field"
        name={name}
        required={required}
        type={type}
        onBlur={onBlur}
        onChange={onFormChange}
        onFocus={onFocus}
        onKeyPress={handleKeyPress}
        pattern={resolvedPattern.current}
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={String(value ?? '')}
        clearField={() => onChange('')}
      />
    );
  }
);

FormControl.displayName = 'FormControl';
